/* global globalThis */
// Compact, account-scoped project metadata and text notes. No file payloads.
import { listQueuedProjectFiles } from './projectFileQueue';
import { openOfflineDatabase } from './sqliteDatabase';
const PREFIX = 'upya.offline.project.v1:';
const DATABASE = 'upya_projects';
const accountKey = (value) => {
  if (value === undefined || value === null || !String(value).trim()) throw new Error('An account is required');
  return String(value);
};
const projectKey = (value) => {
  if (value === undefined || value === null || !String(value).trim()) throw new Error('A project is required');
  return String(value);
};
const snapshot = (project) => ({
  id: String(project.id), name: project.name, instructions: project.instructions || '',
  role: project.role, shared: project.shared, created_at: project.created_at,
  updated_at: project.updated_at, file_count: project.file_count ?? project.files?.length ?? 0,
  files: (project.files || []).map((file) => ({ id: file.id || file.file_id,
    name: file.name, size: file.size, mime_type: file.mime_type, category: file.category,
    uploaded_at: file.uploaded_at, preview: (file.preview || file.text || '').slice(0, 180) })),
});

export function createBrowserProjectStore(storage = globalThis.localStorage) {
  const prefix = (account) => `${PREFIX}${encodeURIComponent(account)}:`;
  const key = (account, id) => `${prefix(account)}${encodeURIComponent(id)}`;
  return {
    async list(account) {
      const result = [];
      for (let i = 0; i < storage.length; i += 1) {
        const name = storage.key(i);
        if (name?.startsWith(prefix(account))) result.push(JSON.parse(storage.getItem(name)));
      }
      return result;
    },
    async get(account, id) { const raw = storage.getItem(key(account, id)); return raw ? JSON.parse(raw) : null; },
    async put(account, project) { storage.setItem(key(account, project.id), JSON.stringify(project)); },
    async remove(account, id) { storage.removeItem(key(account, id)); },
    async clear(account) {
      const keys = [];
      for (let i = 0; i < storage.length; i += 1) if (storage.key(i)?.startsWith(prefix(account))) keys.push(storage.key(i));
      keys.forEach((keyName) => storage.removeItem(keyName));
    },
  };
}

export async function createSQLiteProjectStore(db) {
  await db.execute(`CREATE TABLE IF NOT EXISTS offline_projects (
    account_id TEXT NOT NULL, project_id TEXT NOT NULL, snapshot TEXT NOT NULL,
    PRIMARY KEY (account_id, project_id)
  );`);
  return {
    async list(account) {
      const result = await db.query('SELECT snapshot FROM offline_projects WHERE account_id = ?', [account]);
      return (result.values || []).map((row) => JSON.parse(row.snapshot));
    },
    async get(account, id) {
      const result = await db.query('SELECT snapshot FROM offline_projects WHERE account_id = ? AND project_id = ?', [account, id]);
      return result.values?.[0] ? JSON.parse(result.values[0].snapshot) : null;
    },
    async put(account, project) {
      await db.run('INSERT OR REPLACE INTO offline_projects (account_id, project_id, snapshot) VALUES (?, ?, ?)',
        [account, project.id, JSON.stringify(project)]);
    },
    async remove(account, id) { await db.run('DELETE FROM offline_projects WHERE account_id = ? AND project_id = ?', [account, id]); },
    async clear(account) { await db.run('DELETE FROM offline_projects WHERE account_id = ?', [account]); },
  };
}

async function platformStore() {
  const { Capacitor } = await import('@capacitor/core');
  if (Capacitor.getPlatform() !== 'android') return createBrowserProjectStore();
  return createSQLiteProjectStore(await openOfflineDatabase(DATABASE));
}

export function createProjectRepository(getStore = platformStore) {
  let storePromise;
  let queue = Promise.resolve();
  const store = () => {
    if (!storePromise) storePromise = Promise.resolve().then(getStore).catch((error) => { storePromise = null; throw error; });
    return storePromise;
  };
  const serial = (work) => {
    const result = queue.then(work);
    queue = result.catch(() => {});
    return result;
  };
  return {
    listProjects(userId) { return serial(async () => (await (await store()).list(accountKey(userId))).sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))); },
    getProject(userId, id) { return serial(async () => (await store()).get(accountKey(userId), projectKey(id))); },
    cacheProject(userId, project) {
      return serial(async () => {
        const account = accountKey(userId); const id = projectKey(project?.id); const driver = await store();
        const previous = await driver.get(account, id);
        const next = { ...snapshot(project), files: project.files ? snapshot(project).files : previous?.files || [], notes: previous?.notes || [],
          pendingInstructions: previous?.pendingInstructions || null };
        if (next.pendingInstructions) next.instructions = next.pendingInstructions.value;
        await driver.put(account, next);
        return next;
      });
    },
    cacheProjectList(userId, projects) {
      return serial(async () => {
        const account = accountKey(userId); const driver = await store();
        const queuedFiles = globalThis.indexedDB ? await listQueuedProjectFiles(account).catch(() => null) : [];
        const queuedProjectIds = queuedFiles && new Set(queuedFiles.map((file) => file.projectId));
        for (const project of projects) {
          const id = projectKey(project.id); const previous = await driver.get(account, id);
          const next = { ...snapshot(project), files: project.files ? snapshot(project).files : previous?.files || [], notes: previous?.notes || [],
            pendingInstructions: previous?.pendingInstructions || null };
          if (next.pendingInstructions) next.instructions = next.pendingInstructions.value;
          await driver.put(account, next);
        }
        // Only an authoritative server list may remove vanished projects;
        // never discard a project's unsynced notes.
        const ids = new Set(projects.map((project) => String(project.id)));
        for (const row of await driver.list(account)) {
          if (!ids.has(row.id) && queuedProjectIds && !queuedProjectIds.has(row.id) && !row.pendingInstructions && !(row.notes || []).some((note) => note.pending)) await driver.remove(account, row.id);
        }
      });
    },
    mergeServerNotes(userId, id, notes) {
      return serial(async () => {
        const account = accountKey(userId); const driver = await store(); const row = await driver.get(account, projectKey(id));
        if (!row) return null;
        const pending = (row.notes || []).filter((note) => note.pending && !notes.some((item) => item.id === note.id));
        const next = { ...row, notes: [...notes.map((note) => ({ ...note, pending: false })), ...pending] };
        await driver.put(account, next);
        return next;
      });
    },
    addNote(userId, id, text) {
      return serial(async () => {
        const account = accountKey(userId); const driver = await store(); const row = await driver.get(account, projectKey(id));
        if (!row) throw new Error('Project must be available locally');
        if (!['owner', 'editor'].includes(row.role)) throw new Error('Project is read-only');
        const body = String(text || '').trim();
        if (!body || body.length > 5000) throw new Error('Note must contain 1 to 5000 characters');
        const note = { id: `n_${globalThis.crypto?.randomUUID?.().replace(/-/g, '') || `${Date.now()}_${Math.random().toString(36).slice(2)}`}`,
          project_id: row.id, text: body, created_at: new Date().toISOString(), pending: true };
        await driver.put(account, { ...row, notes: [...(row.notes || []), note] });
        return note;
      });
    },
    setInstructions(userId, id, value) {
      return serial(async () => {
        const account = accountKey(userId); const driver = await store(); const row = await driver.get(account, projectKey(id));
        if (!row) throw new Error('Project must be available locally');
        if (!['owner', 'editor'].includes(row.role)) throw new Error('Project is read-only');
        const instructions = String(value ?? '');
        if (instructions.length > 2000) throw new Error('Context is too long');
        const pendingInstructions = { value: instructions, revision: globalThis.crypto?.randomUUID?.() || `${Date.now()}_${Math.random()}` };
        const next = { ...row, instructions, pendingInstructions };
        await driver.put(account, next);
        return next;
      });
    },
    acknowledgeInstructions(userId, id, revision, serverProject) {
      return serial(async () => {
        const account = accountKey(userId); const driver = await store(); const row = await driver.get(account, projectKey(id));
        if (!row || row.pendingInstructions?.revision !== revision) return row;
        const next = { ...row, instructions: serverProject.instructions || '', pendingInstructions: null,
          updated_at: serverProject.updated_at || row.updated_at };
        await driver.put(account, next);
        return next;
      });
    },
    acknowledgeNote(userId, id, serverNote) {
      return serial(async () => {
        const account = accountKey(userId); const driver = await store(); const row = await driver.get(account, projectKey(id));
        if (!row) return;
        await driver.put(account, { ...row, notes: (row.notes || []).map((note) => note.id === serverNote.id ? { ...serverNote, pending: false } : note) });
      });
    },
    clearAccount(userId) { return serial(async () => (await store()).clear(accountKey(userId))); },
    hasProjects(userId) { return this.listProjects(userId).then((rows) => rows.length > 0); },
  };
}

const repository = createProjectRepository();
export const listProjects = (...args) => repository.listProjects(...args);
export const getProject = (...args) => repository.getProject(...args);
export const cacheProject = (...args) => repository.cacheProject(...args);
export const cacheProjectList = (...args) => repository.cacheProjectList(...args);
export const mergeServerNotes = (...args) => repository.mergeServerNotes(...args);
export const addNote = (...args) => repository.addNote(...args);
export const setProjectInstructions = (...args) => repository.setInstructions(...args);
export const acknowledgeProjectInstructions = (...args) => repository.acknowledgeInstructions(...args);
export const acknowledgeNote = (...args) => repository.acknowledgeNote(...args);
export const clearAccountProjects = (...args) => repository.clearAccount(...args);
export const hasOfflineProjects = (...args) => repository.hasProjects(...args);
